import React from 'react';

/**
 * Stored rich text (TipTap or converted markdown HTML) as React elements for
 * a read-only card preview. Only a fixed set of tags is kept and no attributes
 * are copied, so whatever the HTML holds, nothing in it runs or links out:
 * shared boards show other people's documents through this.
 */

const BLOCK_TAGS: Record<string, string> = {
  p: 'p', h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6',
  ul: 'ul', ol: 'ol', li: 'li', blockquote: 'blockquote', pre: 'pre',
  table: 'table', thead: 'thead', tbody: 'tbody', tr: 'tr', th: 'th', td: 'td',
};
const INLINE_TAGS: Record<string, string> = {
  strong: 'strong', b: 'strong', em: 'em', i: 'em', u: 'u', s: 's', del: 's', strike: 's',
  code: 'code', mark: 'mark', sub: 'sub', sup: 'sup',
  // Links read as links but don't navigate from inside a card
  a: 'span',
};
// Whitespace between these tags' children is formatting, not text
const NO_TEXT_PARENTS = new Set(['ul', 'ol', 'table', 'thead', 'tbody', 'tr']);
const DROP_TAGS = new Set(['script', 'style', 'iframe', 'object', 'embed', 'template', 'img', 'svg', 'video', 'audio']);

function convert(node: Node, key: string): React.ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    const parent = node.parentElement?.tagName.toLowerCase() ?? '';
    if (NO_TEXT_PARENTS.has(parent) && !(node.textContent ?? '').trim()) return null;
    return node.textContent;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const el = node as Element;
  const tag = el.tagName.toLowerCase();
  if (DROP_TAGS.has(tag)) return null;
  if (tag === 'br') return <br key={key} />;
  if (tag === 'hr') return <hr key={key} />;

  const children = Array.from(el.childNodes).map((child, i) => convert(child, `${key}.${i}`));

  // TipTap task items: show the checkbox state
  if (tag === 'li' && el.getAttribute('data-type') === 'taskItem') {
    const checked = el.getAttribute('data-checked') === 'true';
    return (
      <li key={key} data-task={checked ? 'done' : 'open'}>
        {children}
      </li>
    );
  }

  const mapped = BLOCK_TAGS[tag] ?? INLINE_TAGS[tag];
  if (!mapped) return <React.Fragment key={key}>{children}</React.Fragment>;
  return React.createElement(mapped, { key, className: tag === 'a' ? 'doc-preview-link' : undefined }, children);
}

/**
 * The first `maxBlocks` top-level blocks of `html` as safe React elements.
 * Cards clip to their height anyway; the cap keeps long documents cheap.
 */
export function renderHtmlPreview(html: string | undefined, maxBlocks = 24): React.ReactNode[] {
  if (!html || typeof DOMParser === 'undefined') return [];
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const blocks = Array.from(doc.body.childNodes).filter(
    (n) => n.nodeType === Node.ELEMENT_NODE || (n.textContent ?? '').trim() !== '',
  );
  return blocks.slice(0, maxBlocks).map((n, i) => convert(n, String(i)));
}

/** Words in stored rich text, for documents saved without a count. */
export function countWords(html: string | undefined): number {
  if (!html || typeof DOMParser === 'undefined') return 0;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  // Block boundaries count as spaces, so "deck</p><p>Hi" is two words
  doc.body.querySelectorAll('p, li, h1, h2, h3, h4, h5, h6, br, td, th, blockquote, pre')
    .forEach((el) => el.append(' '));
  return (doc.body.textContent ?? '').split(/\s+/).filter(Boolean).length;
}
